import { useState, useEffect } from 'react';

interface Snapshot {
  id: string;
  timestamp: number;
  config: {
    bedrooms: number;
    bathrooms: number;
    area: number;
    unit: string;
    kitchenType: string;
    extras: string[];
    prompt: string;
  };
  style: string;
}

interface VersionHistoryPanelProps {
  darkMode: boolean;
  onClose: () => void;
  onRestore: (snapshot: Snapshot) => void;
}

const MAX_SNAPSHOTS = 20;
const STORAGE_KEY = 'masterArchitectVersionHistory';

export default function VersionHistoryPanel({ darkMode, onClose, onRestore }: VersionHistoryPanelProps) {
  const [snapshots, setSnapshots] = useState<Snapshot[]>([]);

  useEffect(() => {
    loadSnapshots();
  }, []);

  const loadSnapshots = () => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setSnapshots(parsed);
      } catch (e) {
        console.error('Failed to load version history:', e);
        setSnapshots([]);
      }
    }
  };

  const handleRestore = (snapshot: Snapshot) => {
    onRestore(snapshot);
  };

  const handleClearHistory = () => {
    if (confirm('Are you sure you want to clear all version history? This cannot be undone.')) {
      localStorage.removeItem(STORAGE_KEY);
      setSnapshots([]);
    }
  };

  const getRelativeTime = (timestamp: number) => {
    const now = Date.now();
    const diff = now - timestamp;
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (seconds < 60) return 'Just now';
    if (minutes < 60) return `${minutes} min ago`;
    if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    if (days < 7) return `${days} day${days > 1 ? 's' : ''} ago`;
    return new Date(timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/30 z-50 transition-opacity"
        onClick={onClose}
      ></div>

      {/* Slide-in Panel */}
      <div className={`fixed top-0 right-0 h-full w-96 z-50 shadow-2xl transform transition-transform duration-300 ${
        darkMode ? 'bg-gray-800' : 'bg-white'
      }`}>
        {/* Header */}
        <div className={`flex items-center justify-between p-6 border-b ${
          darkMode ? 'border-gray-700' : 'border-gray-200'
        }`}>
          <div>
            <h3 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Version History
            </h3>
            <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {snapshots.length} auto-saved {snapshots.length === 1 ? 'snapshot' : 'snapshots'}
            </p>
          </div>
          <button
            onClick={onClose}
            className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors cursor-pointer ${
              darkMode ? 'text-gray-400 hover:text-gray-300 hover:bg-gray-700' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
            }`}
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto h-[calc(100vh-140px)] p-6">
          {snapshots.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center py-12">
              <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 ${
                darkMode ? 'bg-gray-700' : 'bg-gray-100'
              }`}>
                <i className={`ri-history-line text-3xl ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}></i>
              </div>
              <h4 className={`text-base font-semibold mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-900'}`}>
                No snapshots yet
              </h4>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Your floor plan changes will be auto-saved here
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {snapshots.map((snapshot, index) => (
                <div
                  key={snapshot.id}
                  className={`rounded-lg border p-4 transition-all hover:shadow-md ${
                    darkMode ? 'bg-gray-700 border-gray-600 hover:border-teal-500' : 'bg-gray-50 border-gray-200 hover:border-teal-400'
                  }`}
                >
                  {/* Timestamp */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${
                        index === 0 ? 'bg-teal-500' : darkMode ? 'bg-gray-600' : 'bg-gray-300'
                      }`}></div>
                      <span className={`text-xs font-medium ${
                        index === 0 
                          ? 'text-teal-500' 
                          : darkMode ? 'text-gray-400' : 'text-gray-500'
                      }`}>
                        {getRelativeTime(snapshot.timestamp)}
                      </span>
                    </div>
                    {index === 0 && (
                      <span className="px-2 py-0.5 bg-teal-500 text-white text-xs rounded-full font-semibold">
                        Latest
                      </span>
                    )}
                  </div>

                  {/* Config Summary */}
                  <div className="space-y-2 mb-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`px-2 py-1 rounded-md text-xs font-medium ${
                        darkMode ? 'bg-gray-600 text-gray-300' : 'bg-white text-gray-700'
                      }`}>
                        <i className="ri-hotel-bed-line mr-1"></i>
                        {snapshot.config.bedrooms} bed
                      </span>
                      <span className={`px-2 py-1 rounded-md text-xs font-medium ${
                        darkMode ? 'bg-gray-600 text-gray-300' : 'bg-white text-gray-700'
                      }`}>
                        <i className="ri-door-line mr-1"></i>
                        {snapshot.config.bathrooms} bath
                      </span>
                      <span className={`px-2 py-1 rounded-md text-xs font-medium ${
                        darkMode ? 'bg-gray-600 text-gray-300' : 'bg-white text-gray-700'
                      }`}>
                        <i className="ri-ruler-line mr-1"></i>
                        {snapshot.config.area} {snapshot.config.unit}
                      </span>
                    </div>
                    {snapshot.style && (
                      <div className="flex items-center gap-2">
                        <i className={`ri-home-smile-line text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}></i>
                        <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          {snapshot.style}
                        </span>
                      </div>
                    )}
                    {snapshot.config.extras.length > 0 && (
                      <div className="flex items-center gap-2">
                        <i className={`ri-add-circle-line text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}></i>
                        <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          {snapshot.config.extras.length} extra{snapshot.config.extras.length > 1 ? 's' : ''}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Restore Button */}
                  <button
                    onClick={() => handleRestore(snapshot)}
                    className={`w-full py-2 rounded-lg font-medium text-sm transition-all cursor-pointer whitespace-nowrap ${
                      darkMode 
                        ? 'bg-teal-600 hover:bg-teal-700 text-white' 
                        : 'bg-teal-500 hover:bg-teal-600 text-white'
                    }`}
                  >
                    <i className="ri-refresh-line mr-2"></i>
                    Restore This Version
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {snapshots.length > 0 && (
          <div className={`p-4 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <button
              onClick={handleClearHistory}
              className={`w-full py-2.5 rounded-lg font-medium text-sm transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 ${
                darkMode 
                  ? 'bg-gray-700 hover:bg-red-900/30 text-red-400 border border-gray-600 hover:border-red-500' 
                  : 'bg-white hover:bg-red-50 text-red-600 border border-gray-300 hover:border-red-300'
              }`}
            >
              <i className="ri-delete-bin-line"></i>
              Clear All History
            </button>
          </div>
        )}
      </div>
    </>
  );
}

// Export utility function to save snapshots
export const saveSnapshot = (config: any, style: string) => {
  const saved = localStorage.getItem(STORAGE_KEY);
  let snapshots: Snapshot[] = [];
  
  if (saved) {
    try {
      snapshots = JSON.parse(saved);
    } catch (e) {
      snapshots = [];
    }
  }

  const newSnapshot: Snapshot = {
    id: Date.now().toString(),
    timestamp: Date.now(),
    config: {
      bedrooms: config.bedrooms,
      bathrooms: config.bathrooms,
      area: config.area,
      unit: config.unit,
      kitchenType: config.kitchenType,
      extras: [...config.extras],
      prompt: config.prompt,
    },
    style: style,
  };

  // Add to beginning of array
  snapshots.unshift(newSnapshot);

  // Keep only last MAX_SNAPSHOTS
  if (snapshots.length > MAX_SNAPSHOTS) {
    snapshots = snapshots.slice(0, MAX_SNAPSHOTS);
  }

  localStorage.setItem(STORAGE_KEY, JSON.stringify(snapshots));
};